import { Link } from 'react-router-dom';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg md:text-xl font-bold text-gray-800 mt-8 mb-3">
      {children}
    </h3>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-gray-600 text-base leading-relaxed mb-4">
      {children}
    </p>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-teal-50 border-l-4 border-teal-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-lightbulb-line text-teal-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-teal-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

interface ProcessCardProps {
  number: string;
  title: string;
  description: string;
}

function ProcessCard({ number, title, description }: ProcessCardProps) {
  return (
    <div className="flex gap-5 items-start">
      <div className="shrink-0 w-10 h-10 flex items-center justify-center bg-gray-900 text-white rounded-xl font-extrabold text-sm">
        {number}
      </div>
      <div>
        <h4 className="font-bold text-gray-900 text-base mb-1">{title}</h4>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

interface SustainCardProps {
  icon: string;
  title: string;
  description: string;
}

function SustainCard({ icon, title, description }: SustainCardProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 flex flex-col gap-3">
      <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl">
        <i className={`${icon} text-teal-500 text-xl`}></i>
      </div>
      <h4 className="font-bold text-gray-900 text-base leading-snug">{title}</h4>
      <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

const processSteps: ProcessCardProps[] = [
  {
    number: '01',
    title: 'Consultation and Design',
    description: 'The journey begins with a thorough consultation where the Pop Pro Tents team works with clients to discuss their event goals, preferences, and logistical considerations. Design experts use advanced software to create detailed 3D renderings of the proposed setup, allowing clients to visualise the final result before installation begins.',
  },
  {
    number: '02',
    title: 'High-Quality Materials and Craftsmanship',
    description: 'Pop Pro Tents sources premium, weather-resistant fabric that ensures durability and longevity. This fabric is designed to withstand wind, rain, and UV exposure, and every element, from tent fabric to structural components, is crafted with precision and care.',
  },
  {
    number: '03',
    title: 'Expert Installation and Support',
    description: 'The expert installation team is trained to handle all aspects of setting up and securing stretch tents. The process is carried out with meticulous attention to detail, ensuring the tent is properly anchored and configured to meet safety and aesthetic requirements.',
  },
  {
    number: '04',
    title: 'Customisation and Innovation',
    description: 'Pop Pro Tents offers a wide range of customisation options, from bespoke tent designs to unique colour and branding opportunities. In addition to standard rentals, the company is constantly exploring advanced lighting systems, climate control solutions, and interactive elements that elevate the event experience.',
  },
];

const sustainCards: SustainCardProps[] = [
  {
    icon: 'ri-recycle-line',
    title: 'Eco-Friendly Materials',
    description: 'Pop Pro Tents sources materials that are durable and recyclable, reducing waste and promoting sustainability across every event installation.',
  },
  {
    icon: 'ri-delete-bin-line',
    title: 'Waste Management',
    description: 'Pop Pro Tents implements waste reduction and recycling practices to ensure that materials are disposed of responsibly after every event.',
  },
];

export default function ArticleBody() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16">

      {/* Hero image */}
      <div className="rounded-2xl overflow-hidden mb-10 w-full h-72 md:h-96 bg-gray-100">
        <img
          src="/assets/img/7d03a1fcabb7.webp"
          alt="Stretch Tent Rentals behind the scene"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Intro */}
      <Paragraph>
        In the ever-evolving world of event planning, one company stands out for its exceptional expertise and commitment to quality: Pop Pro Tents.
      </Paragraph>
      <Paragraph>
        Renowned for their innovative approach to stretch tent rentals, Pop Pro Tents has established itself as a leader in the industry, transforming outdoor spaces into stunning venues for any occasion.
      </Paragraph>
      <Paragraph>
        But what makes them the go-to choice for stretch tent rentals? Join us for an exclusive behind-the-scenes look at how Pop Pro Tents has redefined the art of tent rental, setting the standard for excellence in the industry.
      </Paragraph>

      {/* Evolution */}
      <SectionHeading>The Evolution of Pop Pro Tents</SectionHeading>
      <Paragraph>
        Founded with a vision to revolutionise the event tent rental industry, Pop Pro Tents began as a small startup with a single mission: to provide high-quality, versatile, and aesthetically pleasing stretch tents. Over the years, the company has grown into a leading force in the market, thanks to their dedication to innovation, exceptional customer service, and an unwavering commitment to excellence.
      </Paragraph>
      <Paragraph>
        At the heart of Pop Pro Tents&apos; success is their ability to stay ahead of industry trends and continually adapt to the needs of their clients.
      </Paragraph>
      <Paragraph>
        From humble beginnings to becoming a premier stretch tent rentals company in the USA, Pop Pro Tents has built a reputation for delivering top-notch solutions for events of all sizes and types.
      </Paragraph>

      {/* Magic of Stretch Tents */}
      <SectionHeading>The Magic of Stretch Tents</SectionHeading>
      <Paragraph>
        Bedouin tents and freeform tents are the cornerstone of Pop Pro Tents&apos; offerings.
      </Paragraph>
      <Paragraph>
        These tents are celebrated for their flexibility, durability, and unique aesthetic appeal. Unlike traditional tents, stretch tents are made from a special fabric that allows for a wide range of shapes and configurations, making them ideal for creating distinctive event spaces.
      </Paragraph>
      <Paragraph>
        One of the most compelling features of stretch tents is their ability to adapt to various terrains and environments. Whether it&apos;s a beachside wedding, a corporate event in a park, or a festival in a field, stretch tent rentals can be installed in diverse locations. The fabric&apos;s elasticity allows for a sleek and modern look, and the ability to create sweeping curves and angles adds an element of elegance to any setting.
      </Paragraph>

      <TipBox>
        Stretch tents can be installed in virtually any outdoor setting, from beaches and parks to festival fields, making them one of the most versatile event structures available. Their elastic fabric creates sweeping curves and organic shapes no rigid tent can match.
      </TipBox>

      {/* Crafting the Perfect Experience */}
      <SectionHeading>Crafting the Perfect Stretch Tent Experience</SectionHeading>
      <Paragraph>
        At Pop Pro Tents, every stretch tent rental is a bespoke experience. The team takes pride in working closely with clients to understand their vision and requirements, ensuring that each tent is tailored to meet their specific needs.
      </Paragraph>
      <Paragraph>
        From the initial consultation to the final setup, Pop Pro Tents provides a seamless and personalised service, making the process as smooth and enjoyable as possible.
      </Paragraph>

      <div className="flex flex-col gap-6 mt-8 bg-gray-50 rounded-2xl p-8">
        {processSteps.map((step) => (
          <ProcessCard key={step.number} {...step} />
        ))}
      </div>

      {/* Inline image */}
      <div className="rounded-2xl overflow-hidden my-10 w-full h-64 md:h-80 bg-gray-100">
        <img
          src="/assets/img/fe6b7f94f31f.webp"
          alt="Stretch Tent Rentals"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Sustainability */}
      <SectionHeading>A Commitment to Sustainability</SectionHeading>
      <Paragraph>
        As a forward-thinking company, Pop Pro Tents is also dedicated to sustainability and environmental responsibility. The company takes steps to minimise their environmental impact through various initiatives:
      </Paragraph>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        {sustainCards.map((card) => (
          <SustainCard key={card.title} {...card} />
        ))}
      </div>

      <Paragraph>
        By integrating these sustainable practices into their operations, Pop Pro Tents demonstrates a commitment to not only providing exceptional event solutions but also protecting the environment for future generations.
      </Paragraph>

      {/* Future */}
      <SectionHeading>The Future of Pop Pro Tents</SectionHeading>
      <Paragraph>
        As Pop Pro Tents looks to the future, the company remains committed to innovation, excellence, and customer satisfaction. They continue to explore new technologies and trends in the event industry, ensuring that they stay at the forefront of stretch tent rentals and provide clients with cutting-edge solutions.
      </Paragraph>
      <Paragraph>
        Whether it&apos;s expanding their range of services, enhancing their sustainability efforts, or delivering even more personalised experiences, Pop Pro Tents is dedicated to maintaining their position as a leader in the stretch tent rental industry.
      </Paragraph>
      <Paragraph>
        Their passion for creating extraordinary event spaces and their unwavering commitment to quality ensure that Pop Pro Tents will remain a top choice for event planners and organisers for years to come.
      </Paragraph>

      <TipBox>
        Pop Pro Tents is always innovating, from incorporating advanced lighting and climate control systems to exploring new sustainable materials. If you&apos;re planning an event, their team is ready to bring your vision to life with the latest in stretch tent technology.
      </TipBox>

      {/* Conclusion */}
      <SectionHeading>Conclusion</SectionHeading>
      <Paragraph>
        Pop Pro Tents has truly redefined the world of stretch tent rentals, combining innovation, craftsmanship, and exceptional service to create unforgettable event experiences.
      </Paragraph>
      <Paragraph>
        From their early days as a small startup to becoming a leading stretch tent company in the USA, Pop Pro Tents&apos; dedication to excellence and client satisfaction has set them apart in the industry.
      </Paragraph>
      <Paragraph>
        With a focus on customisation, sustainability, and cutting-edge technology, Pop Pro Tents continues to lead the way in stretch tent rentals, providing versatile and beautiful solutions for any event.
      </Paragraph>
      <Paragraph>
        If you&apos;re looking for a company that can transform your outdoor space into a stunning venue, look no further than Pop Pro Tents, where exceptional event experiences are just a tent away.
      </Paragraph>

      {/* Internal links */}
      <div className="flex flex-wrap gap-3 mt-6">
        <Link
          to="/products/custom-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-tent-line"></i>
          Custom Tents
        </Link>
        <Link
          to="/blog/enhance-festival-appeal-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-article-line"></i>
          Festival Appeal Guide
        </Link>
        <Link
          to="/blog/stretch-tent-rentals-guide-tips-considerations"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-book-open-line"></i>
          Rental Guide
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-line"></i>
          Inquire Now
        </Link>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
        <span className="text-xs text-gray-400 mr-1 self-center">Tags:</span>
        {['Stretch Tents', 'Tent Rentals', 'Event Planning', 'Behind the Scenes', 'Sustainability', 'Innovation'].map((tag) => (
          <span
            key={tag}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
